
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Award, Star, Medal, Trophy, Zap, CheckCircle, TrendingUp, Heart, ThumbsUp, Crown, Gem, Shield, Target, Clock, Users, Gift } from "lucide-react";

export type ProfileBadgeType = 
  | "skill-expert" 
  | "top-rated" 
  | "rising-talent" 
  | "project-completion" 
  | "client-favorite"
  | "long-term"
  | "quick-response"
  | "client-appreciation" // New: For client thank you badges
  | "milestone" // New: For career milestones
  | "elite-talent" // New: For top 1% performers
  | "community-contributor" // New: For platform community contributions
  | "prompt-delivery" // New: For always delivering on time
  | "quality-champion" // New: For exceptionally high quality work
  | "super-communicator"; // New: For excellent communication skills

export interface ProfileBadge {
  id: string;
  type: ProfileBadgeType;
  name: string;
  description: string;
  earnedDate: string;
  level?: 1 | 2 | 3; // badge levels (bronze, silver, gold)
  clientName?: string; // New: For client-given badges
  clientMessage?: string; // New: Custom message from client
  rarity?: "common" | "uncommon" | "rare" | "epic" | "legendary"; // New: Badge rarity
  expiresAt?: string; // New: For temporary badges
}

interface BadgeDisplayProps {
  badge: ProfileBadge;
  size?: "sm" | "md" | "lg";
  showDetails?: boolean; // New: Option to show/hide additional details
}

export const BadgeIcon = ({ type, className = "" }: { type: ProfileBadgeType; className?: string }) => {
  const iconProps = { className: `${className}`, size: 16 };
  
  switch (type) {
    case "skill-expert":
      return <Star {...iconProps} />;
    case "top-rated":
      return <Trophy {...iconProps} />;
    case "rising-talent":
      return <TrendingUp {...iconProps} />;
    case "project-completion":
      return <CheckCircle {...iconProps} />;
    case "client-favorite":
      return <Award {...iconProps} />;
    case "long-term":
      return <Medal {...iconProps} />;
    case "quick-response":
      return <Zap {...iconProps} />;
    // New badge icons
    case "client-appreciation":
      return <Heart {...iconProps} />;
    case "milestone":
      return <Target {...iconProps} />;
    case "elite-talent":
      return <Crown {...iconProps} />;
    case "community-contributor":
      return <Users {...iconProps} />;
    case "prompt-delivery":
      return <Clock {...iconProps} />;
    case "quality-champion":
      return <Gem {...iconProps} />;
    case "super-communicator":
      return <ThumbsUp {...iconProps} />;
    default:
      return <Award {...iconProps} />;
  }
};

export const getBadgeStyles = (type: ProfileBadgeType, level: number = 1, rarity?: string) => {
  const baseClasses = "transition-all duration-300 shadow-sm hover:shadow";
  
  // First, handle rarity-based styling if provided
  if (rarity) {
    switch (rarity) {
      case "legendary":
        return "bg-gradient-to-r from-purple-200 via-gold-200 to-pink-200 text-purple-900 border-purple-400 hover:from-purple-300 hover:via-gold-300 hover:to-pink-300 ring-2 ring-purple-300 ring-opacity-50 " + baseClasses;
      case "epic":
        return "bg-gradient-to-r from-fuchsia-100 to-purple-200 text-fuchsia-900 border-fuchsia-300 hover:from-fuchsia-200 hover:to-purple-300 " + baseClasses;
      case "rare":
        return "bg-gradient-to-r from-indigo-100 to-blue-200 text-indigo-900 border-indigo-300 hover:from-indigo-200 hover:to-blue-300 " + baseClasses;
      case "uncommon":
        return "bg-gradient-to-r from-emerald-100 to-teal-200 text-emerald-900 border-emerald-300 hover:from-emerald-200 hover:to-teal-300 " + baseClasses;
      case "common":
        return "bg-gradient-to-r from-slate-100 to-gray-200 text-slate-800 border-slate-300 hover:from-slate-200 hover:to-gray-300 " + baseClasses;
    }
  }
  
  // Then handle type-based styling
  switch (type) {
    case "skill-expert":
      return level === 3 
        ? "bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border-amber-300 hover:from-amber-200 hover:to-amber-300 " + baseClasses
        : level === 2
          ? "bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 border-slate-300 hover:from-slate-200 hover:to-slate-300 " + baseClasses
          : "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border-orange-300 hover:from-orange-200 hover:to-orange-300 " + baseClasses;
    case "top-rated":
      return "bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-300 hover:from-purple-200 hover:to-purple-300 " + baseClasses;
    case "rising-talent":
      return "bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border-emerald-300 hover:from-emerald-200 hover:to-emerald-300 " + baseClasses;
    case "project-completion":
      return "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300 hover:from-blue-200 hover:to-blue-300 " + baseClasses;
    case "client-favorite":
      return "bg-gradient-to-r from-pink-100 to-pink-200 text-pink-800 border-pink-300 hover:from-pink-200 hover:to-pink-300 " + baseClasses;
    case "long-term":
      return "bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 border-indigo-300 hover:from-indigo-200 hover:to-indigo-300 " + baseClasses;
    case "quick-response":
      return "bg-gradient-to-r from-cyan-100 to-cyan-200 text-cyan-800 border-cyan-300 hover:from-cyan-200 hover:to-cyan-300 " + baseClasses;
    // Styling for new badge types
    case "client-appreciation":
      return "bg-gradient-to-r from-rose-100 to-pink-200 text-rose-800 border-rose-300 hover:from-rose-200 hover:to-pink-300 " + baseClasses;
    case "milestone":
      return "bg-gradient-to-r from-amber-100 to-yellow-200 text-amber-800 border-amber-300 hover:from-amber-200 hover:to-yellow-300 " + baseClasses;
    case "elite-talent":
      return "bg-gradient-to-r from-violet-100 to-purple-200 text-violet-800 border-violet-300 hover:from-violet-200 hover:to-purple-300 " + baseClasses;
    case "community-contributor":
      return "bg-gradient-to-r from-green-100 to-teal-200 text-green-800 border-green-300 hover:from-green-200 hover:to-teal-300 " + baseClasses;
    case "prompt-delivery":
      return "bg-gradient-to-r from-sky-100 to-blue-200 text-sky-800 border-sky-300 hover:from-sky-200 hover:to-blue-300 " + baseClasses;
    case "quality-champion":
      return "bg-gradient-to-br from-amber-100 via-yellow-100 to-orange-200 text-amber-800 border-amber-300 hover:from-amber-200 hover:via-yellow-200 hover:to-orange-300 " + baseClasses;
    case "super-communicator":
      return "bg-gradient-to-r from-blue-100 to-indigo-200 text-blue-800 border-blue-300 hover:from-blue-200 hover:to-indigo-300 " + baseClasses;
    default:
      return "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300 hover:from-gray-200 hover:to-gray-300 " + baseClasses;
  }
};

const BadgeDisplay: React.FC<BadgeDisplayProps> = ({ badge, size = "md", showDetails = false }) => {
  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-1 text-sm",
    lg: "px-3 py-1.5 text-base"
  };

  // New: Add special effects for rare badges
  const rarityEffect = badge.rarity === "legendary" 
    ? "animate-pulse shadow-lg" 
    : badge.rarity === "epic" 
      ? "shadow-md" 
      : "";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            className={`${getBadgeStyles(badge.type, badge.level, badge.rarity)} ${sizeClasses[size]} ${rarityEffect} min-h-10 flex items-center gap-1.5 border animate-fade-in cursor-help`} 
            variant="outline"
          >
            <BadgeIcon type={badge.type} className={badge.level === 3 ? "text-amber-500" : badge.level === 2 ? "text-slate-500" : ""} />
            {badge.name && badge.name.length > 15 ? badge.name.slice(0,15)+"..." : badge.name}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-200 rounded-md">
          <div className="space-y-1.5">
            <div className="font-medium">{badge.name}</div>
            <div className="text-sm text-gray-600">{badge.description}</div>
            
            {/* Display client info for client appreciation badges */}
            {badge.clientName && (
              <div className="text-sm text-rose-600 flex items-center gap-1 mt-1">
                <Heart className="w-3.5 h-3.5" />
                <span>From {badge.clientName}</span>
              </div>
            )}
            
            {/* Display client message if available */}
            {badge.clientMessage && (
              <div className="text-sm italic bg-gray-50 p-2 rounded-sm mt-1 border-l-2 border-gray-200">
                "{badge.clientMessage}"
              </div>
            )}
            
            {/* Display badge rarity if available */}
            {badge.rarity && (
              <div className="flex items-center gap-1.5 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  badge.rarity === "legendary" ? "bg-purple-100 text-purple-700" :
                  badge.rarity === "epic" ? "bg-fuchsia-100 text-fuchsia-700" :
                  badge.rarity === "rare" ? "bg-blue-100 text-blue-700" :
                  badge.rarity === "uncommon" ? "bg-emerald-100 text-emerald-700" :
                  "bg-gray-100 text-gray-700"
                }`}>
                  {badge.rarity.charAt(0).toUpperCase() + badge.rarity.slice(1)}
                </span>
              </div>
            )}
            
            <div className="text-xs text-gray-500 pt-1">Earned on {new Date(badge.earnedDate).toLocaleDateString()}</div>
            
            {/* Show expiration date if applicable */}
            {badge.expiresAt && (
              <div className="text-xs text-orange-500">
                Expires on {new Date(badge.expiresAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BadgeDisplay;
